












































































































import { Component, Vue } from 'vue-property-decorator';
import { mdbBtn, mdbIcon, mdbTooltip, mdbProgress, mdbModal, mdbModalHeader, mdbModalTitle, mdbModalBody, mdbModalFooter, mdbAlert } from '@folky-tools/mdbvue';
import { VueGoodTable } from '@folky-tools/vue-good-table';
import Swal from 'sweetalert2';
import { Characteristic } from '@/models/references-classification/characteristic';
import { UserActionLogInfo } from '@/models/users/user-logging/user-action-log-info';
import { UserActionType } from '@/models/users/user-logging/user-action-type';
import { UserActionEntityType } from '@/models/users/user-logging/user-action-entity-type';
import { UserActionScopeType } from '@/models/users/user-logging/user-action-scope-type';
import { axiosService, usersService, authorizationManagerService, referencesClassificationService, notificationsService } from '@/services';

@Component({
    components: {
        mdbBtn,
        mdbIcon,
        mdbTooltip,
        mdbProgress,
        mdbModal,
        mdbModalHeader,
        mdbModalTitle,
        mdbModalBody,
        mdbModalFooter,
        mdbAlert,
        VueGoodTable,
        FormModal: () => import('@/components/shared/FormModal.vue'),
        EditCharacteristic: () => import('@/components/businesses/EditCharacteristic.vue'),
        AppSysBar: () => import('@/components/shared/AppSysBar.vue')
    }
})
export default class BusinessCharacteristicsView extends Vue {
    private response: any;
    private canEditBusiness = false;
    private rows: any[] = [];
    private columns: any[] = [];
    private totalRecords: number = 0;
    private isLoading: boolean = false;
    private characteristicsPerPage: number = 20;
    private isPageInfoVisible: boolean = false;

    private serverParams: any = {
        columnFilters: {
            name: '',
            values: '',
            referencesCount: ''
        },
        sort: [
            {
                field: 'name',
                type: 'asc'
            }
        ],
        page: 1,
        perPage: this.characteristicsPerPage
    };

    private selectedCharacteristic: Characteristic = new Characteristic();
    private selectedCharacteristicCopy: Characteristic = new Characteristic();

    private showEditCharacteristicModal: boolean = false;
    private savingCharacteristic: boolean = false;

    private businessId: number = 0;

    private async created(): Promise<void> {
        this.businessId = +this.$route.params.id;
        this.canEditBusiness = authorizationManagerService.isAdmin || authorizationManagerService.authorizedBusinessIdsToEdit.some(id => id === this.businessId);
    }

    private async beforeMount(): Promise<void> {
        await this.setColumns();
        await this.loadItems();
    }

    private async mounted(): Promise<void> {
        if (this.$route.query.businessName) {
            this.$root.$emit('change-active-page', 'views.businesses.businessCharacteristicsView.title', this.$route.query.businessName.toString());
        } else {
            this.$root.$emit('change-active-page', 'views.businesses.businessCharacteristicsView.title', '...');
        }

        await usersService.logUserAction(
            new UserActionLogInfo({
                type: UserActionType.HasVisited,
                entity: UserActionEntityType.Businesses,
                scope: UserActionScopeType.BusinessReferenceCharacteristics,
                entityId: +this.$route.params.id
            })
        );
    }

    private async onPageChange(params: any): Promise<void> {
        this.serverParams.page = params.currentPage;
        await this.loadItems();
    }

    private async onPerPageChange(params: any): Promise<void> {
        this.serverParams.perPage = params.currentPerPage;
        await this.loadItems();
    }

    private async onSortChange(params: any): Promise<void> {
        if (params[0].field !== this.serverParams.sort[0].field || params[0].type !== this.serverParams.sort[0].type) {
            this.serverParams.sort[0].field = params[0].field;
            this.serverParams.sort[0].type = params[0].type;
            await this.loadItems();
        }
    }

    private async onColumnFilter(params: any): Promise<void> {
        if (params.columnFilters.name !== this.serverParams.columnFilters.name) {
            this.serverParams.page = 1;
            this.serverParams.columnFilters = JSON.parse(JSON.stringify(params.columnFilters));
            await this.loadItems();
        } else {
            this.serverParams.page = 1;
            this.updateParams(params);
            await this.loadItems();
        }
    }

    private updateParams(newProps: any): void {
        this.serverParams = { ...this.serverParams, ...newProps };
    }

    private async loadItems(): Promise<void> {
        this.isLoading = true;

        const response: any = await axiosService.instance.get(`references-classification/organization/characteristics/query?businessId=${this.businessId}`, {
            params: await this.getQueryParams(this.serverParams.sort, this.serverParams.page, this.serverParams.perPage)
        });

        this.rows = [];

        this.totalRecords = response.data.count;
        response.data.items.forEach((item: any) => {
            this.rows.push(new Characteristic(item));
        });

        this.isLoading = false;
    }

    private async getQueryParams(sortOrder: any, currentPage: number, perPage: number): Promise<any> {
        return {
            $skip: (currentPage - 1) * perPage,
            $top: perPage,
            $orderby: this.getQueryOrderFromColumn(sortOrder),
            $filter: await this.getQueryFilterFromColumns()
        };
    }

    private getQueryOrderFromColumn(sortOrder: any): string | null {
        if (sortOrder && sortOrder.length === 1) {
            return `${sortOrder[0].field} ${sortOrder[0].type}`;
        } else {
            return null;
        }
    }

    private async getQueryFilterFromColumns(): Promise<string | null> {
        let queryFilter = '';
        const logicalOperatorBetweenFilters = ' and ';

        for (const key of Object.keys(this.serverParams.columnFilters)) {
            if (this.serverParams.columnFilters[key]) {
                if (key === 'name') {
                    queryFilter += `${logicalOperatorBetweenFilters}contains(name, '${this.serverParams.columnFilters[key].replace('\'', '\'\'')}')`;
                }

                if (key === 'values') {
                    const attributeIds =
                        authorizationManagerService.isAdmin || authorizationManagerService.canReadMultipleBusinesses
                            ? await referencesClassificationService.getCharacteristicIdsByValueName(null, this.serverParams.columnFilters[key])
                            : await referencesClassificationService.getCharacteristicIdsByValueName(this.businessId, this.serverParams.columnFilters[key]);

                    if (attributeIds && attributeIds.length > 0) {
                        let attributeValuesFilter = '';
                        attributeIds.forEach(id => {
                            attributeValuesFilter += ` or id eq ${id}`;
                        });

                        queryFilter += `${logicalOperatorBetweenFilters}(${attributeValuesFilter.substring(4)})`;
                    } else {
                        queryFilter += `${logicalOperatorBetweenFilters}id eq -1`;
                    }
                }
            }
        }

        return queryFilter === '' ? null : queryFilter.substring(logicalOperatorBetweenFilters.length);
    }

    private setColumns(): void {
        this.columns = [
            {
                field: 'id',
                type: 'number',
                sortable: true
            },
            {
                field: 'name',
                sortable: true,
                filterOptions: {
                    enabled: true,
                    placeholder: this.$t('common.placeholders.searchByName'),
                    filterValue: ''
                }
            },
            {
                field: 'values',
                sortable: false,
                filterOptions: {
                    enabled: true,
                    placeholder: this.$t('common.placeholders.searchByValue'),
                    filterValue: ''
                }
            },
            {
                field: 'referencesCount',
                type: 'number',
                sortable: false
            }
        ];

        if (this.canEditBusiness) {
            this.columns.push({ field: 'actions-slot', sortable: false });
        }
    }

    private addCharacteristic(): void {
        this.selectedCharacteristic = new Characteristic({ businessId: +this.$route.params.id });
        this.selectedCharacteristicCopy = this.selectedCharacteristic.clone();
        this.showEditCharacteristicModal = true;
    }

    private editCharacteristic(characteristic: Characteristic): void {
        this.selectedCharacteristic = characteristic;
        this.selectedCharacteristicCopy = this.selectedCharacteristic.clone();
        this.showEditCharacteristicModal = true;
    }

    private async saveCharacteristic(): Promise<void> {
        this.savingCharacteristic = true;

        if ((this.$refs.editCharacteristic as any).hasErrors()) {
            this.savingCharacteristic = false;
            return;
        }

        this.selectedCharacteristic.values = this.selectedCharacteristic.values.filter(v => v.name).sort((a, b) => (a.name! > b.name! ? 1 : b.name! > a.name! ? -1 : 0));

        try {
            if (this.selectedCharacteristic.id > 0) {
                const result = await referencesClassificationService.updateCharacteristic(this.selectedCharacteristic);

                if (result) {
                    this.selectedCharacteristicCopy = this.selectedCharacteristic.clone();
                    this.closeCharacteristicEdition();
                    notificationsService.showToastSuccessNotification('views.businesses.businessCharacteristicsView.notifications.characteristicSavedSuccess.existingCharacteristic');

                    await usersService.logUserAction(
                        new UserActionLogInfo({
                            type: UserActionType.HasChanged,
                            entity: UserActionEntityType.Businesses,
                            scope: UserActionScopeType.BusinessReferenceCharacteristics,
                            entityId: +this.$route.params.id
                        })
                    );

                    return;
                }
                notificationsService.showToastErrorNotification();
            } else {
                const result = await referencesClassificationService.newCharacteristic(this.selectedCharacteristic);
                if (result) {
                    this.selectedCharacteristicCopy = this.selectedCharacteristic.clone();
                    this.closeCharacteristicEdition();
                    notificationsService.showToastSuccessNotification('views.businesses.businessCharacteristicsView.notifications.characteristicSavedSuccess.newCharacteristic');
                    await this.loadItems();

                    await usersService.logUserAction(
                        new UserActionLogInfo({
                            type: UserActionType.HasCreated,
                            entity: UserActionEntityType.Businesses,
                            scope: UserActionScopeType.BusinessReferenceCharacteristics,
                            entityId: +this.$route.params.id
                        })
                    );

                    return;
                }
                notificationsService.showToastErrorNotification();
            }
        } catch {
            notificationsService.showToastErrorNotification();
        } finally {
            this.savingCharacteristic = false;
        }
    }

    private closeCharacteristicEdition(): void {
        this.selectedCharacteristic.name = this.selectedCharacteristicCopy.name;
        this.selectedCharacteristic.values = this.selectedCharacteristicCopy.values;
        this.showEditCharacteristicModal = false;
    }

    private async deleteCharacteristic(characteristic: Characteristic): Promise<void> {
        const questionResult = await Swal.fire({
            title: this.$i18n.t('views.businesses.businessCharacteristicsView.actions.delete.modal.title').toString(),
            html: '<h3>' + characteristic.name + '</h3>' + this.$i18n.t('views.businesses.businessCharacteristicsView.actions.delete.modal.textHtml').toString(),
            type: 'warning',
            showCancelButton: true,
            reverseButtons: true,
            focusConfirm: false,
            focusCancel: true,
            confirmButtonText: this.$i18n.t('views.businesses.businessCharacteristicsView.actions.delete.modal.confirm.doConfirm').toString(),
            cancelButtonText: this.$i18n.t('views.businesses.businessCharacteristicsView.actions.delete.modal.doCancel').toString(),
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-danger'
            },
            animation: false
        });

        if (questionResult.value) {
            this.isLoading = true;
            if (await referencesClassificationService.deleteCharacteristic(characteristic)) {
                this.isLoading = false;
                notificationsService.showToastSuccessNotification('views.businesses.businessCharacteristicsView.actions.delete.modal.confirm.successMessage');
                await this.loadItems();

                return;
            }
            this.isLoading = false;
            notificationsService.showToastErrorNotification();
        }
    }
}
